/**
 * Web Speech synthesis capability probe.
 *
 * SPEC §2.6 makes audio mandatory and §5.1 gets it free from `speechSynthesis`.
 * That is the most fragile assumption in the app (risk R1 in docs/DECISIONS.md),
 * so this module's job is to find out the truth on the actual device rather
 * than assume it:
 *
 *  - `getVoices()` is populated asynchronously. Called on first paint it
 *    returns an empty list, which is indistinguishable from "no voices
 *    installed" unless you wait for `voiceschanged`.
 *  - A voice's existence does not mean it works offline. `localService` is the
 *    only signal separating on-device synthesis from a network round trip, and
 *    an app that claims to work offline cannot rely on the network one.
 *  - Some engines list a voice that never actually speaks, so the probe ends by
 *    speaking a silent test utterance and waiting for the engine to confirm.
 *
 * SPEC §2.6: an item with no working audio is excluded from L4 scheduling. It
 * is never silently downgraded to a text card.
 */

export type SpeechSupport =
  /** A voice for this language exists and the engine completed an utterance. */
  | 'ready'
  /** The API exists but has no usable voice for this language. */
  | 'no-voice'
  /**
   * A voice exists and the engine accepted the utterance, but never finished
   * it inside the deadline. This is the Android failure mode that makes voice
   * enumeration useless on its own — see R1.
   */
  | 'dead'
  /** `speechSynthesis` is not available at all. */
  | 'unsupported';

export interface VoiceReport {
  support: SpeechSupport;
  voiceName: string | null;
  /** False means the voice may need the network — see R1. */
  localService: boolean;
  voiceCount: number;
  probedAt: number;
  /**
   * How long the test utterance actually took to reach `onend`, in ms, or null
   * if it never did.
   *
   * The verdict only records whether it beat the deadline; the *number* is what
   * answered the open question in the device matrix — was 500 ms right on a
   * cheap Android? It was not: the first real row came back at 932 ms and
   * 999 ms on working on-device voices, which is only visible because the
   * elapsed time is kept rather than just the verdict.
   */
  onendMs: number | null;
}

const VOICES_TIMEOUT_MS = 2_000;

/**
 * How long the probe waits for `onend` before declaring the engine dead for
 * this session.
 *
 * **This was 500 ms until the device matrix answered the question it was
 * collected to answer.** The first real row, 2026-08-13, Chrome 151 on Android:
 * en-US completed in **932 ms** and ja-JP in **999 ms**, both on-device voices
 * (`localService: true`) that genuinely spoke. Under a 500 ms deadline that
 * phone lost L4 dictation *and* the mora-timing drills in both languages while
 * owning working offline voices for both — the precise false negative D29
 * warned the number might produce.
 *
 * So 500 ms was wrong, and the reasoning behind it ("an engine that cannot
 * finish a zero-volume full stop in half a second will not deliver a dictation
 * card either") is refuted by measurement: nearly all of that second is engine
 * *start-up*, paid once, not per syllable.
 *
 * **2,000 ms**, for three reasons. It is twice the slowest engine yet observed
 * to work, which leaves room for a slower phone than the one we have seen. It
 * matches `VOICES_TIMEOUT_MS`, so the two halves of the probe give up on the
 * same schedule rather than on two different guesses. And the cost of the extra
 * 1.5 s is nothing a learner waits on: the probe runs *after* first paint (see
 * `probeOnBoot`), audio is withheld until it answers, and the ≤3 s
 * icon-tap-to-first-question budget never touches it.
 *
 * What the deadline is *for* is unchanged, and is why loosening it is safe: it
 * detects the engine that announces itself and never finishes. An engine that
 * fires `onend` has, by definition, finished speaking — so a longer wait cannot
 * admit a liar, it can only stop excluding an honest engine that is slow.
 *
 * `onend`, specifically — not `onstart`. The engine that lies about audio fires
 * `onstart` and then goes quiet forever; only completion proves it spoke.
 */
export const TTS_ONEND_DEADLINE_MS = 2_000;

const synth = (): SpeechSynthesis | null =>
  typeof globalThis.speechSynthesis === 'undefined' ? null : globalThis.speechSynthesis;

/**
 * How long the very first `getVoices()` call took, in ms.
 *
 * M4 measured this at ~15 seconds on an environment with no speech service
 * behind the API — the main thread gone, five times the entire icon-tap budget
 * (R1). The probe is deferred past first paint because of it, and the deferral
 * is a mitigation, not a fix: the stall still happens, just somewhere the
 * learner is already looking at a question.
 *
 * Nothing in the app branches on this. It exists so the diagnostics screen can
 * report a real number from a real device, because the open question in the
 * matrix is whether that measurement reproduces on hardware at all.
 */
let firstCallMs: number | null = null;

/** The measurement above, or null if `getVoices()` has not been called yet. */
export const firstCallLatencyMs = (): number | null => firstCallMs;

const timedGetVoices = (speech: SpeechSynthesis): SpeechSynthesisVoice[] => {
  if (firstCallMs !== null) return speech.getVoices();
  const started = performance.now();
  const voices = speech.getVoices();
  firstCallMs = performance.now() - started;
  return voices;
};

/** BCP-47 prefix match: `en` accepts `en-US`, `en-GB`, `en_AU`. */
const matchesLanguage = (voiceLang: string, language: string): boolean =>
  voiceLang.toLowerCase().replace('_', '-').startsWith(language.toLowerCase());

/**
 * Waits for the voice list to populate. Resolves with whatever exists when the
 * timeout expires rather than hanging — a device with no voices must reach a
 * verdict too.
 */
export const listVoices = async (
  language?: string,
  timeoutMs = VOICES_TIMEOUT_MS,
): Promise<SpeechSynthesisVoice[]> => {
  const speech = synth();
  if (!speech) return [];

  const filter = (voices: SpeechSynthesisVoice[]) =>
    language === undefined ? voices : voices.filter((voice) => matchesLanguage(voice.lang, language));

  const immediate = filter(timedGetVoices(speech));
  if (immediate.length > 0) return immediate;

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(timer);
      speech.removeEventListener?.('voiceschanged', onChange);
      resolve(filter(speech.getVoices()));
    };
    const onChange = () => {
      if (filter(speech.getVoices()).length > 0) finish();
    };
    // Belt and braces: some engines never fire the event but do fill the list.
    const poll = setInterval(onChange, 100);
    const timer = setTimeout(finish, timeoutMs);
    speech.addEventListener?.('voiceschanged', onChange);
  });
};

/** Prefers an on-device voice; falls back to any voice for the language. */
export const pickVoice = (
  voices: readonly SpeechSynthesisVoice[],
): SpeechSynthesisVoice | null =>
  voices.find((voice) => voice.localService) ?? voices[0] ?? null;

/**
 * Speaks a zero-volume utterance and waits for the engine to *finish* it.
 *
 * Resolving on `onend` alone is the whole point. An engine that fires `onstart`
 * and then never completes has told us nothing about whether a learner would
 * hear anything, and on Android that is a real configuration, not a hypothetical
 * one. Anything that has not completed inside the deadline is dead.
 */
const utteranceCompletes = async (
  voice: SpeechSynthesisVoice,
  timeoutMs = TTS_ONEND_DEADLINE_MS,
): Promise<{ completed: boolean; elapsedMs: number | null }> => {
  const speech = synth();
  if (!speech || typeof globalThis.SpeechSynthesisUtterance === 'undefined') {
    return { completed: false, elapsedMs: null };
  }
  const started = performance.now();

  return new Promise((resolve) => {
    let settled = false;
    const finish = (completed: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      speech.cancel();
      resolve({ completed, elapsedMs: completed ? performance.now() - started : null });
    };

    const utterance = new SpeechSynthesisUtterance('.');
    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.volume = 0;
    utterance.onend = () => finish(true);
    utterance.onerror = () => finish(false);

    const timer = setTimeout(() => finish(false), timeoutMs);
    speech.speak(utterance);
  });
};

export interface ProbeOptions {
  voicesTimeoutMs?: number;
  /** Defaults to `TTS_ONEND_DEADLINE_MS`. */
  utteranceTimeoutMs?: number;
  /**
   * How long `probeOnBoot` waits before probing, where `requestIdleCallback`
   * is unavailable. Defaults to `IDLE_FALLBACK_MS`.
   */
  deferMs?: number;
}

export const probeVoice = async (
  language: string,
  options: ProbeOptions = {},
): Promise<VoiceReport> => {
  const probedAt = Date.now();
  const absent = { voiceName: null, localService: false, voiceCount: 0, probedAt, onendMs: null };
  if (!synth()) return { support: 'unsupported', ...absent };

  const voices = await listVoices(language, options.voicesTimeoutMs);
  const voice = pickVoice(voices);
  if (!voice) return { support: 'no-voice', ...absent };

  const { completed, elapsedMs } = await utteranceCompletes(voice, options.utteranceTimeoutMs);
  return {
    support: completed ? 'ready' : 'dead',
    voiceName: voice.name,
    localService: voice.localService,
    voiceCount: voices.length,
    probedAt,
    onendMs: elapsedMs,
  };
};

// ------------------------------------------------------- the session verdict

/**
 * The probe runs **once per app boot**, and its answer stands for the whole
 * session.
 *
 * Re-probing per card would be worse in both directions: it costs a settle
 * delay on every audio item, and a device that answers "ready" once and "dead"
 * the next time would flip the L4 rung in and out under the learner. One
 * verdict per session is a stable contract that the rest of the app can gate on
 * (`ladderCeiling` in src/core/ladder.ts).
 */
/**
 * Keyed by language, because voice availability is. R1's specific warning is
 * that `ja-JP` is the voice most likely to be absent on a cheap Android ROM —
 * so a device that speaks English fine may have nothing for Japanese, and a
 * single app-wide verdict would let the English voice vouch for it. That would
 * report audio as ready, schedule L4 dictation and mora minimal-pair drills, and
 * then have nothing to speak them with: the silent degradation §2.6 forbids.
 *
 * D29's guarantee is unchanged in the sense that matters — one verdict per
 * language per app start, so the L4 rung still cannot flicker under the learner.
 */
const verdicts = new Map<string, VoiceReport>();
const inFlight = new Map<string, Promise<VoiceReport>>();

/**
 * Probes on boot and caches the verdict for the session. Concurrent callers
 * share the one probe; later callers get the cached answer without speaking
 * again.
 *
 * **Callers must not run this until the first screen is painted.** Measured, not
 * assumed: the first touch of `speechSynthesis` in an environment with no speech
 * service behind it **blocks the main thread for ~15 seconds** — five times SPEC
 * §5.4's entire icon-tap-to-first-question budget, with the app frozen for all
 * of it. A device with no speech service is not a hypothetical; it is precisely
 * the device R1 is about, so this is a cost real learners would pay.
 *
 * `idle()` below is a second line of defence, not the first: `requestIdleCallback`
 * fires during the gaps in an async boot, which are exactly the moments the app
 * is waiting on IO and about to need the main thread again. The reliable signal
 * is the app telling us a screen is up — see `markInteractive` in src/App.tsx.
 *
 * Until it answers, `isTtsLive()` is false: audio is withheld rather than
 * assumed, which is the same safe default the rest of §2.6 runs on.
 */
export const probeOnBoot = async (
  language: string,
  options: ProbeOptions = {},
): Promise<VoiceReport> => {
  const settled = verdicts.get(language);
  if (settled) return settled;

  const running = inFlight.get(language);
  if (running) return running;

  const probe = idle(options.deferMs)
    .then(() => probeVoice(language, options))
    .then((report) => {
      verdicts.set(language, report);
      inFlight.delete(language);
      return report;
    });
  inFlight.set(language, probe);
  return probe;
};

/** Long enough that the first question is painted, where there is no idle API. */
export const IDLE_FALLBACK_MS = 1_500;

/**
 * The hard ceiling on the wait, because `requestIdleCallback`'s own `timeout`
 * is not one.
 *
 * Measured 2026-08-19, Chromium 148: **a hidden page never runs an idle
 * callback at all**, and the `timeout` option only counts down while the page
 * is visible. A tab that booted in the background sat on the home screen's
 * *"Mengecek suara di HP ini…"* for 25 s and counting, and resolved correctly
 * the moment it was looked at. The old comment here claimed this timeout was
 * "a ceiling, not a target"; for a hidden page it was neither.
 */
export const IDLE_CEILING_MS = 3_000;

const pageIsHidden = (): boolean => globalThis.document?.visibilityState === 'hidden';

/**
 * Resolves once the main thread has nothing better to do — and no later than
 * `IDLE_CEILING_MS` after the page is being looked at.
 *
 * **Waiting for visibility is deliberate and is not the bug above.** The first
 * `speechSynthesis` call on a device with no speech service blocks the main
 * thread for ~15 s (see R1), so this probe may only run when it is cheap. A
 * hidden page will get its probe on return; what it must not do is wait
 * unbounded once the learner is actually there.
 */
const idle = (deferMs = IDLE_FALLBACK_MS): Promise<void> =>
  new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const schedule = () => {
      if (typeof globalThis.requestIdleCallback !== 'function') {
        setTimeout(finish, deferMs);
        return;
      }
      globalThis.requestIdleCallback(() => finish(), { timeout: IDLE_CEILING_MS });
      // The ceiling the line above only pretends to be.
      setTimeout(finish, IDLE_CEILING_MS);
    };

    if (pageIsHidden()) {
      globalThis.document?.addEventListener(
        'visibilitychange',
        () => {
          if (!pageIsHidden()) schedule();
        },
        { once: true },
      );
      return;
    }
    schedule();
  });

/**
 * The session's verdict, or null if the probe has not answered yet.
 *
 * Test seam, alongside `resetTtsVerdict`. The ladder asks `isTtsLive`, the home
 * screen is handed the report it was probed with, and the diagnostics screen
 * deliberately runs its own probe from inside a tap rather than reading a
 * stored one (D29) — so nothing in the app reads this, and the speech tests
 * need it to see what the probe actually recorded.
 */
export const ttsReport = (language: string): VoiceReport | null =>
  verdicts.get(language) ?? null;

/**
 * SPEC §2.6: a dead engine means audio must come from a pre-cached clip or the
 * item is withheld. Unknown (probe still running) is treated as *not* live —
 * the honest default, since claiming audio we cannot deliver is the failure
 * this whole module exists to prevent.
 */
export const isTtsLive = (language: string): boolean =>
  verdicts.get(language)?.support === 'ready';

/**
 * Adopts a verdict produced by an explicit learner action — the "Uji audio"
 * button on the diagnostics screen — and reports whether it was taken up.
 *
 * This is D29's recorded cost being paid back. The boot probe cannot speak from
 * inside a user gesture, so on iOS Safari, which requires one, it marks the
 * engine dead where audio would in fact have worked. A probe run from inside a
 * tap is the one context that requirement is satisfied in, so its answer is
 * worth more than the boot probe's — and it arrives because the learner asked,
 * so the rung appearing is a response to their action rather than the mid-session
 * flicker D29 exists to prevent.
 *
 * Two rules keep it honest:
 *
 *  - **It can only raise capability.** A `dead` reading from a later probe does
 *    not retract an engine that has already been heard to complete an utterance;
 *    that would be exactly the flicker, and a transient failure is not evidence
 *    the device cannot speak.
 *  - **It is held to the same deadline as everything else.** A run that finishes
 *    at 900 ms is reported as the number it is and is *not* adopted, because L4
 *    is scheduled against `TTS_ONEND_DEADLINE_MS` and quietly admitting a slower
 *    engine would put dictation cards in front of a learner who has to wait for
 *    them.
 */
export const adoptVerdict = (language: string, report: VoiceReport): boolean => {
  if (report.support !== 'ready') return false;
  if (report.onendMs !== null && report.onendMs > TTS_ONEND_DEADLINE_MS) return false;
  if (verdicts.get(language)?.support === 'ready') return false;
  verdicts.set(language, report);
  return true;
};

/** Test seam: the verdict is module state and would otherwise leak between cases. */
export const resetTtsVerdict = (): void => {
  verdicts.clear();
  inFlight.clear();
  firstCallMs = null;
};

// ------------------------------------------------------------------ speaking

export interface SpeakOptions {
  rate?: number;
  /** Skip the voice lookup when the caller already probed. */
  voice?: SpeechSynthesisVoice | null;
}

/**
 * Speaks text, resolving when the engine finishes. Resolves (rather than
 * rejecting) when speech is unavailable: audio is an enhancement at every rung
 * except L4, and L4 items are filtered out upstream.
 */
export const speak = async (
  text: string,
  language: string,
  options: SpeakOptions = {},
): Promise<void> => {
  const speech = synth();
  if (!speech || typeof globalThis.SpeechSynthesisUtterance === 'undefined') return;

  const voice = options.voice ?? pickVoice(await listVoices(language));
  if (!voice) return;

  speech.cancel();
  await new Promise<void>((resolve) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = voice;
    utterance.lang = voice.lang;
    // A shade under natural pace: learners need the word, not the performance.
    utterance.rate = options.rate ?? 0.95;
    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();
    speech.speak(utterance);
  });
};

export const cancelSpeech = (): void => {
  synth()?.cancel();
};
